/* Yamazumi — Takt / Cycle / Line balancing chart with headcount calc */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

const ELEMENT_TYPES = [
  { id: "VA",   label: "Value-Add",      color: "#16A34A" },
  { id: "NVA",  label: "Non-Value-Add",  color: "#DC2626" },
  { id: "BNVA", label: "Business-NVA",   color: "#D97706" },
  { id: "WAIT", label: "Wait",           color: "#6B7280" },
];
const TYPE_COLOR = Object.fromEntries(ELEMENT_TYPES.map(t => [t.id, t.color]));

function calcTakt(demand, availMin) {
  const d = Number(demand || 0); const a = Number(availMin || 0);
  return d > 0 ? a / d : 0;
}

export default function YamazumiWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "setup", label: "Demand & Takt",
      render: ({ data, updateData }) => {
        const takt = calcTakt(data.demand_per_period, data.available_minutes);
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="yam-setup">
            <div className="md:col-span-3"><label className="block text-xs font-semibold text-sage-600 mb-1">Process / line</label><input value={data.line_name || ""} onChange={e => updateData({ line_name: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Customer demand (units / period)</label><input data-testid="yam-demand" type="number" value={data.demand_per_period || 0} onChange={e => updateData({ demand_per_period: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Available time (min / period)</label><input data-testid="yam-avail" type="number" value={data.available_minutes || 0} onChange={e => updateData({ available_minutes: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Period type</label><input value={data.period || "shift"} onChange={e => updateData({ period: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="shift / day / week" /></div>
            <div className="md:col-span-3 bg-sage-50 rounded-md p-3 flex items-center gap-3" data-testid="yam-takt-display">
              <span className="text-xs uppercase tracking-wider font-semibold text-sage-500">Takt time</span>
              <span className="font-heading text-3xl font-bold text-sage-900">{takt.toFixed(2)}</span>
              <span className="text-sm text-sage-600">min / unit</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "stations", label: "Stations & Elements",
      render: ({ data, updateData }) => {
        const stations = data.stations || [];
        const addStation = () => updateData({ stations: [...stations, { id: `s-${Date.now()}`, name: `Station ${stations.length + 1}`, elements: [] }] });
        const updStation = (i, p) => updateData({ stations: stations.map((s, idx) => idx === i ? { ...s, ...p } : s) });
        const rmStation  = (i) => updateData({ stations: stations.filter((_, idx) => idx !== i) });
        const addEl = (i) => updStation(i, { elements: [...stations[i].elements, { task: "", time_min: 0, type: "VA" }] });
        const updEl = (i, j, p) => updStation(i, { elements: stations[i].elements.map((e, idx) => idx === j ? { ...e, ...p } : e) });
        const rmEl  = (i, j) => updStation(i, { elements: stations[i].elements.filter((_, idx) => idx !== j) });
        return (
          <div className="space-y-2" data-testid="yam-stations">
            {stations.map((s, i) => {
              const total = s.elements.reduce((a, e) => a + Number(e.time_min || 0), 0);
              return (
                <div key={s.id} className="bg-white border border-sage-200 rounded-md p-3 space-y-2" data-testid={`yam-station-${i}`}>
                  <div className="flex items-center gap-2">
                    <input data-testid={`yam-station-${i}-name`} value={s.name} onChange={e => updStation(i, { name: e.target.value })} className="border border-sage-200 rounded-md px-2 py-1.5 text-sm font-semibold flex-1" />
                    <span className="text-xs text-sage-500">Total: <span className="font-mono font-bold text-sage-800">{total.toFixed(2)} min</span></span>
                    <button onClick={() => rmStation(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-1">
                    {s.elements.length > 0 && (
                      <div className="hidden md:grid grid-cols-12 gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-sage-500">
                        <div className="md:col-span-7">Task / element</div>
                        <div className="md:col-span-2 text-right pr-2">Time (min)</div>
                        <div className="md:col-span-2">Type (VA/NVA/BNVA/Wait)</div>
                        <div className="md:col-span-1"></div>
                      </div>
                    )}
                    {s.elements.map((el, j) => (
                      <div key={j} className="grid grid-cols-1 md:grid-cols-12 gap-1 items-center bg-sage-50 rounded-md p-1.5">
                        <input data-testid={`yam-el-${i}-${j}-task`} placeholder="Task / element" value={el.task} onChange={e => updEl(i, j, { task: e.target.value })} className="md:col-span-7 border border-sage-200 rounded-md px-2 py-1 text-xs bg-white" />
                        <input type="number" step="0.01" placeholder="min" value={el.time_min} onChange={e => updEl(i, j, { time_min: Number(e.target.value) })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1 text-xs bg-white font-mono text-right" />
                        <select value={el.type} onChange={e => updEl(i, j, { type: e.target.value })} className="md:col-span-2 border-2 rounded-md px-2 py-1 text-xs bg-white" style={{ borderColor: TYPE_COLOR[el.type] }}>
                          {ELEMENT_TYPES.map(t => <option key={t.id} value={t.id}>{t.id}</option>)}
                        </select>
                        <button onClick={() => rmEl(i, j)} className="text-red-500 justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <button data-testid={`yam-station-${i}-add-element`} onClick={() => addEl(i)} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-2 py-1 text-xs hover:bg-sage-50 w-full justify-center"><Plus className="w-3 h-3" /> Add task element</button>
                  </div>
                </div>
              );
            })}
            <button data-testid="yam-add-station" onClick={addStation} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add station</button>
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "Yamazumi & Balance",
      render: ({ data }) => {
        const stations = data.stations || [];
        const takt = calcTakt(data.demand_per_period, data.available_minutes);
        const stationData = stations.map(s => {
          const breakdown = ELEMENT_TYPES.reduce((acc, t) => {
            acc[t.id] = s.elements.filter(e => e.type === t.id).reduce((a, e) => a + Number(e.time_min || 0), 0);
            return acc;
          }, {});
          const total = Object.values(breakdown).reduce((a, v) => a + v, 0);
          return { ...s, breakdown, total };
        });
        const longest = Math.max(takt, ...stationData.map(s => s.total), 0.01);
        const sumCT = stationData.reduce((a, s) => a + s.total, 0);
        const requiredOps = takt > 0 ? sumCT / takt : 0;
        const W = 800, H = 320, P = 40;
        const colW = stationData.length ? (W - P * 2) / stationData.length * 0.7 : 0;
        const cx = (i) => P + (i + 0.5) * ((W - P * 2) / Math.max(1, stationData.length));
        const yScale = (v) => H - P - (v / longest) * (H - P * 2);
        return (
          <div className="space-y-4" data-testid="yam-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Takt</p><p className="font-heading text-2xl font-bold text-sage-900">{takt.toFixed(2)} <span className="text-sm font-normal text-sage-500">min</span></p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Sum CT</p><p className="font-heading text-2xl font-bold text-sage-900">{sumCT.toFixed(2)}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Required ops</p><p className="font-heading text-2xl font-bold text-sage-900">{requiredOps.toFixed(2)}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Stations</p><p className="font-heading text-2xl font-bold text-sage-900">{stationData.length}</p></div>
            </div>
            {stationData.length > 0 && (
              <div className="bg-white border border-sage-200 rounded-md p-3 overflow-x-auto" data-testid="yam-chart">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
                  <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#94A3A0" strokeWidth="1" />
                  <line x1={P} y1={P / 2} x2={P} y2={H - P} stroke="#94A3A0" strokeWidth="1" />
                  <line x1={P} y1={yScale(takt)} x2={W - P} y2={yScale(takt)} stroke="#DC2626" strokeDasharray="4,3" strokeWidth="1.4" />
                  <text x={W - P + 2} y={yScale(takt) + 3} fontSize="9" fill="#DC2626" fontWeight="bold">Takt {takt.toFixed(1)}</text>
                  {stationData.map((s, i) => {
                    let bottom = H - P;
                    return (
                      <g key={s.id}>
                        {ELEMENT_TYPES.map(t => {
                          const v = s.breakdown[t.id] || 0; if (v === 0) return null;
                          const h = (v / longest) * (H - P * 2);
                          const yTop = bottom - h; bottom = yTop;
                          return <rect key={t.id} x={cx(i) - colW / 2} y={yTop} width={colW} height={h} fill={t.color} />;
                        })}
                        <text x={cx(i)} y={H - P + 12} textAnchor="middle" fontSize="9" fill="#475569">{(s.name || "").slice(0, 10)}</text>
                        <text x={cx(i)} y={yScale(s.total) - 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1E293B">{s.total.toFixed(1)}</text>
                      </g>
                    );
                  })}
                </svg>
                <div className="flex flex-wrap gap-3 justify-center text-xs">
                  {ELEMENT_TYPES.map(t => (<span key={t.id} className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }}></span>{t.label}</span>))}
                </div>
              </div>
            )}
            <table className="w-full text-xs border border-sage-100 rounded-md overflow-hidden">
              <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider"><tr><th className="text-left px-2 py-1.5">Station</th><th className="text-right px-2 py-1.5">CT (min)</th><th className="text-right px-2 py-1.5">vs Takt</th><th className="text-left px-2 py-1.5">Status</th></tr></thead>
              <tbody>{stationData.map(s => {
                const delta = takt > 0 ? s.total - takt : 0;
                const pct = takt > 0 ? (s.total / takt) * 100 : 0;
                const bottle = takt > 0 && s.total > takt;
                return (
                  <tr key={s.id} className={`border-t border-sage-100 ${bottle ? "bg-red-50/50" : ""}`}>
                    <td className="px-2 py-1 font-semibold text-sage-900">{s.name}</td>
                    <td className="text-right px-2 py-1 font-mono">{s.total.toFixed(2)}</td>
                    <td className="text-right px-2 py-1 font-mono">{pct.toFixed(0)}% ({delta > 0 ? "+" : ""}{delta.toFixed(2)})</td>
                    <td className="px-2 py-1">{bottle ? <span className="text-red-700 font-semibold">Bottleneck</span> : (pct < 70 ? <span className="text-amber-700 font-semibold">Under-loaded</span> : <span className="text-green-700 font-semibold">Balanced</span>)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
            <button data-testid="export-yam-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish Yamazumi" />;
}

export { calcTakt, ELEMENT_TYPES };
